/**
 * Modal d'édition d'un groupe
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGroups } from "@core/hooks/useGroups";
import { X, Save } from "lucide-react";
import { useToast } from "@core/hooks/use-toast";
import { UsageAnalyticsTracker } from "@core/services/UsageAnalyticsTracker";

interface GroupEditorProps {
  groupId: string;
  onClose: () => void;
}

/**
 * Valeur sentinelle passée comme `groupId` pour une CRÉATION.
 *
 * Exportée pour que l'appelant ne la réinvente pas : la page Groupes écrivait
 * `"new"` en dur des deux côtés, et rien ne reliait cette chaîne au composant
 * censé la comprendre — qui, de fait, ne la comprenait pas.
 */
export const NOUVEAU_GROUPE = "new";

export function GroupEditor({ groupId, onClose }: GroupEditorProps) {
  const { t } = useTranslation("polls");
  const { getGroup, updateGroup, createGroup } = useGroups();
  const { toast } = useToast();
  // `"new"` est la valeur que la page Groupes passe pour une CRÉATION. Elle le
  // faisait déjà, mais cet éditeur ne savait que mettre à jour : `getGroup`
  // renvoyait undefined et le composant sortait sur `return null`. Le bouton
  // « Créer un groupe » n'ouvrait donc rien, sans erreur ni trace en console.
  // Le défaut est antérieur à l'essai ; il était invisible parce que la page
  // entière vivait derrière le mur payant, et que la création réelle passait
  // par le sélecteur de la création de sondage.
  const isNew = groupId === NOUVEAU_GROUPE;
  const group = isNew ? undefined : getGroup(groupId);

  const [name, setName] = useState(group?.name || "");
  const [emailsText, setEmailsText] = useState(
    group?.member_emails.join(", ") || "",
  );
  const [saving, setSaving] = useState(false);

  // Fonction de validation d'email avec regex
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fonction pour valider tous les emails et retourner les erreurs
  const validateEmails = (
    emails: string[],
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    emails.forEach((email, index) => {
      if (!validateEmail(email)) {
        if (email.length === 0) {
          errors.push(t("groups.email_error_empty", { index: index + 1 }));
        } else if (!email.includes("@")) {
          errors.push(t("groups.email_error_missing_at", { email }));
        } else if (!email.includes(".")) {
          errors.push(t("groups.email_error_missing_domain", { email }));
        } else if (email.startsWith("@")) {
          errors.push(t("groups.email_error_missing_name", { email }));
        } else if (email.endsWith("@")) {
          errors.push(
            t("groups.email_error_missing_domain_after_at", { email }),
          );
        } else if (email.includes("@@")) {
          errors.push(t("groups.email_error_double_at", { email }));
        } else {
          errors.push(t("groups.email_error_invalid_format", { email }));
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  // États pour les digests hebdomadaires
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState(
    group?.weekly_digest_enabled || false,
  );
  const [digestDay, setDigestDay] = useState(group?.digest_day || 1); // 1=Lundi
  const [digestInterval, setDigestInterval] = useState(
    group?.digest_interval || 1,
  ); // 1=chaque semaine

  useEffect(() => {
    if (group) {
      setName(group.name);
      setEmailsText(group.member_emails.join(", "));
    }
  }, [group]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: t("groups.name_required_title"),
        description: t("groups.name_required_desc"),
        variant: "destructive",
      });
      return;
    }

    const emails = emailsText
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    if (emails.length === 0) {
      toast({
        title: t("groups.emails_required_title"),
        description: t("groups.emails_required_desc"),
        variant: "destructive",
      });
      return;
    }

    // Validation des formats d'email
    const emailValidation = validateEmails(emails);
    if (!emailValidation.isValid) {
      const errorMessage =
        emailValidation.errors.length > 3
          ? t("groups.emails_invalid_count", {
              count: emailValidation.errors.length,
            })
          : t("groups.emails_invalid_list", {
              errors: emailValidation.errors.join(", "),
            });

      toast({
        title: t("groups.email_format_invalid_title"),
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    let enregistre = null;
    let refus: string | null = null;
    try {
      enregistre = isNew
        ? await createGroup({ name: name.trim(), member_emails: emails })
        : await updateGroup(groupId, {
            name: name.trim(),
            member_emails: emails,
            // Les champs de digest ne sont plus écrits : l'éditeur ne les
            // expose plus. Ne PAS les remettre à null ici — ce serait effacer
            // le réglage d'un compte qui l'aurait posé avant le retrait.
          });
    } catch (e) {
      // `createGroup` LÈVE quand le plan ne permet pas la création (c'est un
      // choix de ce hook, pour qu'un appelant ne puisse pas l'ignorer). Le
      // message porte la raison ; l'avaler afficherait « erreur » là où la
      // personne a besoin de savoir que c'est une question de plan.
      refus = e instanceof Error ? e.message : String(e);
    }
    setSaving(false);

    if (enregistre) {
      toast({
        title: isNew ? t("groups.created_title") : t("groups.updated_title"),
        description: t(isNew ? "groups.created_desc" : "groups.updated_desc", {
          name: enregistre.name,
          count: enregistre.member_emails.length,
        }),
      });
      if (isNew) {
        void UsageAnalyticsTracker.track("feature_used", { feature: "group" });
      }
      onClose();
    } else {
      toast({
        title: isNew
          ? t("groups.create_error_title")
          : t("groups.update_error_title"),
        description:
          refus ??
          (isNew
            ? t("groups.create_error_desc")
            : t("groups.update_error_desc")),
        variant: "destructive",
      });
    }
  };

  // Une création n'a pas de groupe à charger : seule une ÉDITION dont la cible
  // a disparu doit sortir ici.
  if (!isNew && !group) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="group-editor-modal"
    >
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {isNew ? t("groups.create_title") : t("groups.edit_title")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-full transition-colors"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("groups.name_label")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("groups.name_placeholder")}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
              data-testid="group-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("groups.emails_label")}
            </label>
            <textarea
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              placeholder="alice@example.com, bob@example.com, charlie@example.com"
              rows={6}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-transparent font-mono text-sm"
              data-testid="group-email-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("groups.member_count", {
                count: emailsText
                  .split(",")
                  .map((e) => e.trim())
                  .filter(Boolean).length,
              })}
            </p>
          </div>
        </div>

        {/* Digest hebdomadaire : interrupteur RETIRÉ le 16/09/2026.

            supabase/functions/send-weekly-digest interroge trois tables qui
            n'ont jamais existé dans cette base (date_polls, date_poll_votes,
            date_poll_group_members). La fonctionnalité est à ÉCRIRE, pas à
            finir, et rien ne relie aujourd'hui polls à date_poll_groups —
            « les sondages du groupe » ne sont définis nulle part.

            L'interrupteur promettait donc un e-mail qui ne serait jamais
            parti. Une promesse fausse à l'intérieur du produit, au moment où
            la personne agit, est pire qu'une promesse fausse sur une page de
            tarifs.

            Ce qui reste en place et reste correct : les colonnes
            weekly_digest_enabled / digest_day / digest_interval /
            next_digest_due_at, la vue active_digest_groups et le trigger
            trg_update_next_digest_due. Aucune donnée n'est détruite, et les 0
            groupes concernés le restent. Remettre cette section le jour où la
            fonction existe est un copier-coller depuis git.

            Détail complet : Docs/2026-09-15-CONSTATS-Hors-Perimetre-Essai-Solo.md §1 */}

        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cancel-group-button"
          >
            {t("groups.cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            data-testid="save-group-button"
          >
            <Save className="w-4 h-4" />
            {saving ? t("groups.saving") : t("groups.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
